// Scoring configuration
const SCORING_CONFIG = {
    disasterOverlapEvents: {
        complete: {
            points: 5,
            tag: 'disaster_overlap_complete'
        },
        partial: {
            points: 3,
            tag: 'disaster_overlap_partial'
        },
        none: {
            points: 0,
            tag: 'disaster_overlap_none'
        }
    },
    disasterOverlapTiming: {
        matching: {
            points: 3,
            tag: 'trend_start_matching'
        },
        zipFirst: {
            points: 2,
            tag: 'trend_start_zip_first'
        },
        fipsFirst: {
            points: 1,
            tag: 'trend_start_fips_first'
        }
    },
    changeComparison: {
        similar: {
            points: 5,
            tag: 'changes_similar'     // Zip and FIPS changes within 10% of each other
        },
        zipHigher: {
            points: 3,
            tag: 'zip_change_higher'   // Zip shows more significant change
        },
        fipsHigher: {
            points: 1,
            tag: 'fips_change_higher'  // FIPS shows more significant change
        }
    },
    changeMagnitude: {
        extreme: {
            points: 7,
            tag: 'change_magnitude_extreme'     // > 50% change
        },
        significant: {
            points: 3,
            tag: 'change_magnitude_significant' // 25-50% change
        },
        moderate: {
            points: 2,
            tag: 'change_magnitude_moderate'    // 10-25% change
        },
        small: {
            points: 1,
            tag: 'change_magnitude_small'       // < 10% change
        }
    },
    missingData: {
        fipsMissing: {
            points: 2,
            tag: 'fips_missing'    // FIPS missing indicates less reliable county-level impact
        },
        zipMissing: {
            points: 1,
            tag: 'zip_missing'     // ZIP missing indicates less reliable local impact
        },
        bothMissing: {
            points: 0,
            tag: 'both_missing'    // Both missing suggests no concerning trend in either geography
        }
    }
};

// Helper functions
const calculateDisasterOverlap = (zipDisasters, fipsDisasters, declaredDisasters) => {
    const zipSet = new Set(zipDisasters);
    const fipsSet = new Set(fipsDisasters);
    const intersection = new Set([...zipSet].filter(x => fipsSet.has(x)));
    const overlapPercentage = intersection.size / Math.max(zipSet.size, fipsSet.size);

    // Get overlap score based on percentage
    let overlapResult;
    if (overlapPercentage === 1) {
        overlapResult = SCORING_CONFIG.disasterOverlapEvents.complete;
    } else if (overlapPercentage > 0) {
        overlapResult = SCORING_CONFIG.disasterOverlapEvents.partial;
    } else {
        overlapResult = SCORING_CONFIG.disasterOverlapEvents.none;
    }

    // Get earliest disaster for each geography
    const getEarliestDisaster = (disasters) => {
        if (!disasters.length) return null;

        return disasters
            .map(id => declaredDisasters.find(d => d.disasterNumber === id))
            .filter(Boolean)
            .sort((a, b) => new Date(a.declarationDate) - new Date(b.declarationDate))[0];
    };

    const earliestZipDisaster = getEarliestDisaster(zipDisasters);
    const earliestFipsDisaster = getEarliestDisaster(fipsDisasters);

    let timingResult = {
        points: 0,
        tag: null,
        trendStartSame: false,
        zipStartDisaster: earliestZipDisaster?.disasterNumber || null,
        fipsStartDisaster: earliestFipsDisaster?.disasterNumber || null
    };

    if (earliestZipDisaster && earliestFipsDisaster) {
        const zipDate = new Date(earliestZipDisaster.declarationDate);
        const fipsDate = new Date(earliestFipsDisaster.declarationDate);

        if (zipDate.getTime() === fipsDate.getTime()) {
            timingResult = {
                ...timingResult,
                points: SCORING_CONFIG.disasterOverlapTiming.matching.points,
                tag: SCORING_CONFIG.disasterOverlapTiming.matching.tag,
                trendStartSame: true
            };
        } else if (zipDate < fipsDate) {
            timingResult = {
                ...timingResult,
                points: SCORING_CONFIG.disasterOverlapTiming.zipFirst.points,
                tag: SCORING_CONFIG.disasterOverlapTiming.zipFirst.tag,
                trendStartSame: false
            };
        } else {
            timingResult = {
                ...timingResult,
                points: SCORING_CONFIG.disasterOverlapTiming.fipsFirst.points,
                tag: SCORING_CONFIG.disasterOverlapTiming.fipsFirst.tag,
                trendStartSame: false
            };
        }
    }

    return {
        hasOverlap: intersection.size > 0,
        overlapScore: overlapResult.points,
        overlapTag: overlapResult.tag,
        timingScore: timingResult.points,
        timingTag: timingResult.tag,
        trendStartSame: timingResult.trendStartSame,
        zipStartDisaster: timingResult.zipStartDisaster,
        fipsStartDisaster: timingResult.fipsStartDisaster
    };
};

const calculateRelativeChange = (before, after, badDirection) => {
    // Handle zero or near-zero cases
    if (Math.abs(before) < 0.0001) {
        return after > 0 ? 100 : -100; // Arbitrary large change for zero-start cases
    }

    const percentChange = ((after - before) / Math.abs(before)) * 100;

    // For 'down' bad direction, negative change is concerning
    // For 'up' bad direction, positive change is concerning
    return badDirection === 'down' ? percentChange : -percentChange;
};

const getScaledChange = (window, dataType, badDirection) => {
    let before, after;

    if (['ratio', 'count', 'percentage'].includes(dataType)) {
        before = window.beforeAverage;
        after = window.afterAverage;
    } else if (dataType === 'dollar') {
        before = window.beforeSlope;
        after = window.afterSlope;
    }

    return calculateRelativeChange(before, after, badDirection);
};

// Calculate total possible points dynamically from scoring config
const getMaxPoints = (obj) => {
    if (typeof obj !== 'object') {
        return 0;
    }

    const values = [];
    let sum = 0;

    Object.values(obj).forEach(value => {
        if (typeof value === 'object') {
            if (value.points) {
                values.push(value.points);
            } else {
                sum += getMaxPoints(value);
            }
        }
    });

    return sum + (values.length ? Math.max(...values) : 0);
};

const TOTAL_POSSIBLE_POINTS = getMaxPoints(SCORING_CONFIG);

const getMagnitudeScore = (zipChange, fipsChange) => {
    const maxChange = Math.max(Math.abs(zipChange), Math.abs(fipsChange));

    if (maxChange > 50) {
        return SCORING_CONFIG.changeMagnitude.extreme;
    } else if (maxChange > 25) {
        return SCORING_CONFIG.changeMagnitude.significant;
    } else if (maxChange > 10) {
        return SCORING_CONFIG.changeMagnitude.moderate;
    } else {
        return SCORING_CONFIG.changeMagnitude.small;
    }
};

const compareChangesWithScale = (zipChange, fipsChange) => {
    const relativeDifference = Math.abs((zipChange - fipsChange) / Math.max(Math.abs(zipChange), Math.abs(fipsChange)));

    if (relativeDifference <= 0.1) {  // Within 10%
        return SCORING_CONFIG.changeComparison.similar;
    } else if (zipChange > fipsChange) {
        return SCORING_CONFIG.changeComparison.zipHigher;
    } else {
        return SCORING_CONFIG.changeComparison.fipsHigher;
    }
};

const processWindows = (zipWindow, fipsWindow, dataType, badDirection, declaredDisasters) => {
    const tags = [];

    if (zipWindow && fipsWindow) {
        const overlapResult = calculateDisasterOverlap(
            zipWindow.disasters,
            fipsWindow.disasters,
            declaredDisasters
        );

        if (overlapResult.overlapTag) tags.push(overlapResult.overlapTag);
        if (overlapResult.timingTag) tags.push(overlapResult.timingTag);

        const zipScaledChange = getScaledChange(zipWindow, dataType, badDirection);
        const fipsScaledChange = getScaledChange(fipsWindow, dataType, badDirection);

        const comparisonResult = compareChangesWithScale(zipScaledChange, fipsScaledChange);
        const magnitudeResult = getMagnitudeScore(zipScaledChange, fipsScaledChange);

        tags.push(comparisonResult.tag, magnitudeResult.tag);

        return {
            totalPoints: overlapResult.overlapScore +
                overlapResult.timingScore +
                comparisonResult.points +
                magnitudeResult.points,
            reason: 'full_comparison',
            tags,
            trendStartSame: overlapResult.trendStartSame,
            zipStartDisaster: overlapResult.zipStartDisaster,
            fipsStartDisaster: overlapResult.fipsStartDisaster,
            details: {
                zipChange: zipScaledChange,
                fipsChange: fipsScaledChange,
                overlapScore: overlapResult.overlapScore,
                timingScore: overlapResult.timingScore,
                changeScore: comparisonResult.points,
                magnitudeScore: magnitudeResult.points
            }
        };
    }

    // FIPS missing
    if (!fipsWindow && zipWindow) {
        const zipScaledChange = getScaledChange(zipWindow, dataType, badDirection);
        const magnitudeResult = getMagnitudeScore(zipScaledChange, zipScaledChange);

        tags.push(SCORING_CONFIG.missingData.fipsMissing.tag, magnitudeResult.tag);

        return {
            totalPoints: SCORING_CONFIG.missingData.fipsMissing.points,
            reason: 'fips_missing',
            tags,
            details: {
                zipChange: zipScaledChange,
                magnitudeScore: magnitudeResult.points
            }
        };
    }

    // ZIP missing
    if (!zipWindow && fipsWindow) {
        const fipsScaledChange = getScaledChange(fipsWindow, dataType, badDirection);
        const magnitudeResult = getMagnitudeScore(fipsScaledChange, fipsScaledChange);

        tags.push(SCORING_CONFIG.missingData.zipMissing.tag, magnitudeResult.tag);

        return {
            totalPoints: SCORING_CONFIG.missingData.zipMissing.points,
            reason: 'zip_missing',
            tags,
            details: {
                fipsChange: fipsScaledChange,
                magnitudeScore: magnitudeResult.points
            }
        };
    }

    // Both missing
    return {
        totalPoints: SCORING_CONFIG.missingData.bothMissing.points,
        reason: 'both_missing_no_trend',
        tags: [SCORING_CONFIG.missingData.bothMissing.tag]
    };
};

export default function ScoreMarketData(
    zipData,
    fipsData,
    disasterDeclarations = [], // Make it optional to maintain backward compatibility
    fipsAreaData
) {
    // console.log('ScoreMarketData.js inputs', {
    //     zipData,
    //     fipsData,
    //     disasterDeclarations,
    //     fipsAreaData
    // })

    // Get a COMBINED set of the ids from all of the items in the zipData and fipsData
    const zipIds = new Set(zipData.map(item => item.id));
    const fipsIds = new Set(fipsData.map(item => item.id));
    const combinedMktDatapointIds = new Set([...zipIds, ...fipsIds]);
    const combinedMktDatapointIdsArray = Array.from(combinedMktDatapointIds);

    const individualDatapointScores = combinedMktDatapointIdsArray.map(id => {
        // console.log('Processing ID:', id);

        const zipItem = zipData.find(item => item.id === id);
        const fipsItem = fipsData.find(item => item.id === id);

        if (zipItem && fipsItem) {
            const zipDA = zipItem.disasterAnalysis;
            const fipsDA = fipsItem.disasterAnalysis;
            const badDirection = zipItem.badDirection;
            const dataType = zipItem.dataType;
            const datapointWeight = zipItem.weighting;

            // console.log('Window Indices:', {
            //     zipDA: {
            //         mostSignificantDown: zipDA.mostSignificantDownwardWindowIndex,
            //         mostSignificantUp: zipDA.mostSignificantUpwardWindowIndex,
            //         windowsLength: zipDA.allWindows.length
            //     },
            //     fipsDA: {
            //         mostSignificantDown: fipsDA.mostSignificantDownwardWindowIndex,
            //         mostSignificantUp: fipsDA.mostSignificantUpwardWindowIndex,
            //         windowsLength: fipsDA.allWindows.length
            //     },
            //     badDirection,
            //     dataType
            // });

            // Get relevant windows based on badDirection for both zip and fips
            const zipRelevantWindowIndex = badDirection === 'down' ?
                zipDA?.mostSignificantDownwardWindowIndex :
                zipDA?.mostSignificantUpwardWindowIndex;

            const fipsRelevantWindowIndex = badDirection === 'down' ?
                fipsDA?.mostSignificantDownwardWindowIndex :
                fipsDA?.mostSignificantUpwardWindowIndex;

            // Get the windows if they exist
            const zipWindow = zipRelevantWindowIndex !== null && zipRelevantWindowIndex !== undefined ?
                zipDA?.allWindows[zipRelevantWindowIndex - 1] : null;

            const fipsWindow = fipsRelevantWindowIndex !== null && fipsRelevantWindowIndex !== undefined ?
                fipsDA?.allWindows[fipsRelevantWindowIndex - 1] : null;

            // console.log('Windows found:', {
            //     hasZipWindow: !!zipWindow,
            //     hasFipsWindow: !!fipsWindow,
            //     zipWindowData: zipWindow,
            //     fipsWindowData: fipsWindow
            // });

            // Only process missing windows through scoring system
            if (!zipWindow && !fipsWindow) {
                // console.log('Both windows missing for ID:', id, '- This suggests no concerning trend');
                return {
                    datapointId: id,
                    badDirection,
                    totalPoints: SCORING_CONFIG.missingData.bothMissing.points,
                    percentageOfTotal: (SCORING_CONFIG.missingData.bothMissing.points / TOTAL_POSSIBLE_POINTS) * 100,
                    percentageOfTotalWeighted: ((SCORING_CONFIG.missingData.bothMissing.points / TOTAL_POSSIBLE_POINTS) * 100) * datapointWeight,
                    weighting: datapointWeight,
                    scoringReason: 'both_missing_no_trend',
                    tags: [SCORING_CONFIG.missingData.bothMissing.tag]
                };
            }

            const scoreResult = processWindows(zipWindow, fipsWindow, dataType, badDirection, disasterDeclarations);

            // console.log('Score calculation:', {
            //     hasZipWindow: !!zipWindow,
            //     hasFipsWindow: !!fipsWindow,
            //     scoreReason: scoreResult.reason,
            //     totalPoints: scoreResult.totalPoints
            // });

            return {
                datapointId: id,
                badDirection,
                totalPoints: scoreResult.totalPoints,
                percentageOfTotal: (scoreResult.totalPoints / TOTAL_POSSIBLE_POINTS) * 100,
                percentageOfTotalWeighted: ((scoreResult.totalPoints / TOTAL_POSSIBLE_POINTS) * 100) * datapointWeight,
                weighting: datapointWeight,
                scoringReason: scoreResult.reason,
                tags: scoreResult.tags,
                trendStartSame: scoreResult.trendStartSame,
                zipStartDisaster: scoreResult.zipStartDisaster,
                fipsStartDisaster: scoreResult.fipsStartDisaster,
                details: scoreResult.details
            };
        } else {
            // console.log('No matching items found for ID:', id);
            return null;
        }
    }).filter(Boolean);

    // Sort the individualDatapointScores by totalPoints in descending order
    individualDatapointScores.sort((a, b) => b.totalPoints - a.totalPoints);

    // Get the average of the percentageOfTotalWeighted
    const averagePercentageOfTotalWeighted = individualDatapointScores.reduce((sum, item) => sum + item.percentageOfTotalWeighted, 0) / individualDatapointScores.length;


    // Get the county NRI risk score
    const countyNRIRiskScore = fipsAreaData?.RISK_SCORE;

    // Create a COPY of the averagePercentageOfTotalWeighted
    let adjustedAveragePercentageOfTotalWeighted = averagePercentageOfTotalWeighted;
    if (countyNRIRiskScore) {
        if (countyNRIRiskScore > 99) {
            adjustedAveragePercentageOfTotalWeighted = adjustedAveragePercentageOfTotalWeighted + 20
        } else if (countyNRIRiskScore > 95) {
            adjustedAveragePercentageOfTotalWeighted = adjustedAveragePercentageOfTotalWeighted + 15
        } else if (countyNRIRiskScore > 80) {
            adjustedAveragePercentageOfTotalWeighted = adjustedAveragePercentageOfTotalWeighted + 10
        } else if (countyNRIRiskScore > 50) {
            adjustedAveragePercentageOfTotalWeighted = adjustedAveragePercentageOfTotalWeighted + 3
        } else if (countyNRIRiskScore > 10) {
            adjustedAveragePercentageOfTotalWeighted = adjustedAveragePercentageOfTotalWeighted + 2
        } else {
            adjustedAveragePercentageOfTotalWeighted = adjustedAveragePercentageOfTotalWeighted + 2
        }
    }




    // Create another copy, this time of the adjustedAveragePercentageOfTotalWeighted to check the number of disasters
    let adjustedAveragePercentageOfTotalWeightedWithDisasters = adjustedAveragePercentageOfTotalWeighted;
    if (disasterDeclarations.length > 25) {
        adjustedAveragePercentageOfTotalWeightedWithDisasters = adjustedAveragePercentageOfTotalWeighted + 10;
    } else if (disasterDeclarations.length > 15) {
        adjustedAveragePercentageOfTotalWeightedWithDisasters = adjustedAveragePercentageOfTotalWeighted + 5;
    } else if (disasterDeclarations.length > 10) {
        adjustedAveragePercentageOfTotalWeightedWithDisasters = adjustedAveragePercentageOfTotalWeighted + 2;
    } else if (disasterDeclarations.length > 5) {
        adjustedAveragePercentageOfTotalWeightedWithDisasters = adjustedAveragePercentageOfTotalWeighted + 1;
    }

    // Determine if the adjustment caused the score to cross either the 70 or 30 threshold
    let didCountyNRIRiskCauseLevelChange = false;
    // Determine if the number of disasters caused the score to cross either the 70 or 30 threshold
    let didDisastersCauseLevelChange = false;

    // Set the thresholds
    const HIGH_THRESHOLD = 70;
    const MEDIUM_THRESHOLD = 30;

    // Assign a High, Medium, or Low based on the averagePercentageOfTotalWeighted
    let riskLevelText = 'Low';
    if (adjustedAveragePercentageOfTotalWeightedWithDisasters >= HIGH_THRESHOLD) {
        riskLevelText = 'High';

        if ((adjustedAveragePercentageOfTotalWeighted >= HIGH_THRESHOLD && averagePercentageOfTotalWeighted < HIGH_THRESHOLD)) {

            didCountyNRIRiskCauseLevelChange = true;

        }

        if (adjustedAveragePercentageOfTotalWeighted < HIGH_THRESHOLD) {

            didDisastersCauseLevelChange = true;

        }


    } else if (adjustedAveragePercentageOfTotalWeightedWithDisasters >= MEDIUM_THRESHOLD) {
        riskLevelText = 'Medium';

        if ((adjustedAveragePercentageOfTotalWeighted >= MEDIUM_THRESHOLD && averagePercentageOfTotalWeighted < MEDIUM_THRESHOLD)) {

            didCountyNRIRiskCauseLevelChange = true;

        }

        if (adjustedAveragePercentageOfTotalWeighted < MEDIUM_THRESHOLD) {

            didDisastersCauseLevelChange = true;

        }
    }

    // Determine if all objects in individualDatapointScores have the trendStartSame key present and not null or undefined
    const allTrendStartSame = individualDatapointScores.every(item => item.trendStartSame !== null && item.trendStartSame !== undefined);

    // Get a single set of all of the unique values for the zipStartDisaster and fipsStartDisaster keys assuming they are not null or undefined
    const allUniqueDisasters = new Set([...individualDatapointScores.map(item => item.zipStartDisaster).filter(item => item !== null && item !== undefined), ...individualDatapointScores.map(item => item.fipsStartDisaster).filter(item => item !== null && item !== undefined)]);

    // Get the number of values in allUniqueDisasters
    const numberOfDisasters = allUniqueDisasters.size;

    // Turn the allUniqueDisasters set into an array
    const allUniqueDisastersArray = Array.from(allUniqueDisasters);

    // Find the earliest disaster ID from our set of disaster numbers
    const earliestDisaster = disasterDeclarations
        .filter(d => allUniqueDisastersArray.includes(d.disasterNumber))
        .sort((a, b) => new Date(a.declarationDate) - new Date(b.declarationDate))[0]?.disasterNumber || null;
    
    
    

    const output = {
        riskLevelScore: adjustedAveragePercentageOfTotalWeightedWithDisasters,
        riskLevelText: riskLevelText,
        didCountyNRIRiskCauseLevelChange: didCountyNRIRiskCauseLevelChange,
        didDisastersCauseLevelChange: didDisastersCauseLevelChange,
        individualDatapointScores: individualDatapointScores,
        doAllDatapointsHaveClimateTrends: allTrendStartSame,
        numberOfDisasters: numberOfDisasters,
        listOfDisastersStartingTrends: allUniqueDisastersArray,
        earliestDisaster: earliestDisaster
    }
    // console.log('Final Scores:', output);
    return output;
}